"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var utilities_1 = require("../utilities");
var loadGoogleTagManager_1 = __importDefault(require("./loadGoogleTagManager"));
var AFFILIATION = 'Flow Commerce';
var CONTACT_INFO_STEP = 1;
var SHIPPING_METHOD_STEP = 2;
var PAYMENT_INFO_STEP = 3;
var GoogleTagManager = /** @class */ (function () {
    function GoogleTagManager(options) {
        this.setOptions(options);
    }
    /**
     * Note: The Google Tag Manager snippet must be placed directly in the page
     * that you intend on tracking. Placing it in a hidden iframe or deploying it
     * within another tag management system will prevent certain tags from
     * accurately tracking the parent page.
     * https://developers.google.com/tag-manager/quickstart
     */
    GoogleTagManager.install = function (parameters) {
        parameters.trackers.forEach(function (tracker) {
            loadGoogleTagManager_1.default(tracker.tracker_id);
        });
    };
    GoogleTagManager.prototype.fireEvent = function (entry) {
        if (window.dataLayer == null) {
            window.dataLayer = [];
        }
        window.dataLayer.push(entry);
    };
    GoogleTagManager.prototype.addToCart = function (event) {
        var useBaseCurrency = this.options.useBaseCurrency;
        var item = event.data.item;
        var itemCurrency = item.currency;
        if (useBaseCurrency && item.base_currency != null) {
            itemCurrency = item.base_currency;
        }
        this.fireEvent({
            event: 'flow.add',
            ecommerce: {
                currency: itemCurrency,
                add: {
                    actionField: {
                        affiliation: AFFILIATION,
                        list: 'Checkout',
                    },
                    products: [
                        utilities_1.createProductFromItem(item, useBaseCurrency),
                    ],
                },
            },
        });
    };
    GoogleTagManager.prototype.removeFromCart = function (event) {
        var useBaseCurrency = this.options.useBaseCurrency;
        var item = event.data.item;
        var itemCurrency = item.currency;
        if (useBaseCurrency && item.base_currency != null) {
            itemCurrency = item.base_currency;
        }
        this.fireEvent({
            event: 'flow.remove',
            ecommerce: {
                currency: itemCurrency,
                remove: {
                    actionField: {
                        affiliation: AFFILIATION,
                        list: 'Checkout',
                    },
                    products: [
                        utilities_1.createProductFromItem(item, useBaseCurrency),
                    ],
                },
            },
        });
    };
    GoogleTagManager.prototype.contactInfoStep = function (event) {
        var useBaseCurrency = this.options.useBaseCurrency;
        var order = event.data.order;
        this.fireEvent({
            event: 'flow.checkout',
            ecommerce: {
                currency: utilities_1.getOrderCurrency(order, useBaseCurrency),
                checkout: {
                    actionField: {
                        step: CONTACT_INFO_STEP,
                        affiliation: AFFILIATION,
                    },
                    products: utilities_1.createProductsFromOrder(order, useBaseCurrency),
                },
            },
        });
    };
    GoogleTagManager.prototype.shippingMethodStep = function (event) {
        var useBaseCurrency = this.options.useBaseCurrency;
        var order = event.data.order;
        this.fireEvent({
            event: 'flow.checkout',
            ecommerce: {
                currency: utilities_1.getOrderCurrency(order, useBaseCurrency),
                checkout: {
                    actionField: {
                        step: SHIPPING_METHOD_STEP,
                        affiliation: AFFILIATION,
                    },
                    products: utilities_1.createProductsFromOrder(order, useBaseCurrency),
                },
            },
        });
    };
    GoogleTagManager.prototype.shippingMethodOption = function (order) {
        var _this = this;
        // Note: Google only supports a single discrete option per step, but our
        // checkout supports multiple deliveries.
        utilities_1.getSelectedDeliveryOptions(order).forEach(function (deliveryOption) {
            _this.fireEvent({
                event: 'flow.checkout_option',
                ecommerce: {
                    checkout_option: {
                        actionField: {
                            step: SHIPPING_METHOD_STEP,
                            affiliation: AFFILIATION,
                            option: deliveryOption.tier.name,
                        },
                    },
                },
            });
        });
    };
    GoogleTagManager.prototype.paymentInfoStep = function (event) {
        var useBaseCurrency = this.options.useBaseCurrency;
        var order = event.data.order;
        this.shippingMethodOption(order);
        this.fireEvent({
            event: 'flow.checkout',
            ecommerce: {
                currency: utilities_1.getOrderCurrency(order, useBaseCurrency),
                checkout: {
                    actionField: {
                        step: PAYMENT_INFO_STEP,
                        affiliation: AFFILIATION,
                    },
                    products: utilities_1.createProductsFromOrder(order, useBaseCurrency),
                },
            },
        });
    };
    GoogleTagManager.prototype.paymentMethodOption = function (event) {
        var _this = this;
        var order = event.data.order;
        if (order.payments != null) {
            // Note: Google only supports a single discrete option per step, but our
            // API is designed to support multiple payments.
            order.payments.forEach(function (payment) {
                var option = payment.type;
                if (payment.type === 'online') {
                    option = payment.description;
                }
                _this.fireEvent({
                    event: 'flow.checkout_option',
                    ecommerce: {
                        checkout_option: {
                            actionField: {
                                option: option,
                                affiliation: AFFILIATION,
                                step: PAYMENT_INFO_STEP,
                            },
                        },
                    },
                });
            });
        }
    };
    GoogleTagManager.prototype.purchase = function (event) {
        var order = event.data.order;
        var useBaseCurrency = this.options.useBaseCurrency;
        this.paymentMethodOption(event);
        this.fireEvent({
            event: 'flow.purchase',
            ecommerce: {
                currency: utilities_1.getOrderCurrency(order, useBaseCurrency),
                purchase: {
                    actionField: {
                        id: order.number,
                        affiliation: AFFILIATION,
                        revenue: useBaseCurrency ? order.total.base.amount : order.total.amount,
                        tax: utilities_1.getOrderTaxAmount(order, useBaseCurrency),
                        shipping: utilities_1.getOrderShippingAmount(order, useBaseCurrency),
                        coupon: utilities_1.getOrderDiscountCode(order),
                    },
                    products: utilities_1.createProductsFromOrder(order, useBaseCurrency),
                },
            },
        });
    };
    GoogleTagManager.prototype.setOptions = function (options) {
        var _a = options.useBaseCurrency, useBaseCurrency = _a === void 0 ? false : _a;
        this.options = {
            useBaseCurrency: useBaseCurrency,
        };
    };
    GoogleTagManager.prototype.event = function (event) {
        switch (event.type) {
            case 'add_to_cart':
                this.addToCart(event);
                break;
            case 'remove_from_cart':
                this.removeFromCart(event);
                break;
            case 'purchase':
                this.purchase(event);
                break;
            case 'checkout_init':
                this.contactInfoStep(event);
                break;
            case 'checkout_shipping':
                this.shippingMethodStep(event);
                break;
            case 'checkout_payment':
                this.paymentInfoStep(event);
                break;
            default:
                break;
        }
    };
    return GoogleTagManager;
}());
exports.GoogleTagManager = GoogleTagManager;



//////////////////
// WEBPACK FOOTER
// ./~/@flowio/pixel/dist/gtm/GoogleTagManager.js
// module id = 610
// module chunks = 0 1